import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, Sparkles } from "lucide-react";

/**
 * Autocomplétion d'entreprise (KBO BE + api.gouv FR).
 * Props:
 *   - onSelect(company): remonte {nom, tva, adresse, code_postal, ville, pays}
 *   - compact (bool): version discrète sans encart
 */
export const CompanyAutocomplete = ({ onSelect, compact = false }) => {
  const [country, setCountry] = useState("BE");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async (val) => {
    setQ(val);
    if (!val || val.length < 3) { setRows([]); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/company-lookup?q=${encodeURIComponent(val)}&country=${country}`);
      setRows(r.data.results || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const pick = (c) => {
    onSelect?.(c);
    setQ("");
    setRows([]);
    toast.success(`${c.nom} — ${c.adresse}, ${c.code_postal} ${c.ville}`);
  };

  return (
    <div className={compact ? "space-y-1" : "bg-indigo-50 border border-indigo-100 rounded-lg p-2.5 space-y-1.5"}>
      {!compact && (
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
          <p className="text-xs font-semibold text-indigo-700">Rechercher une entreprise</p>
          <div className="ml-auto flex gap-1">
            <button type="button" onClick={() => { setCountry("BE"); setRows([]); }} className={`text-[10px] px-1.5 py-0.5 rounded-full ${country === "BE" ? "bg-indigo-600 text-white" : "bg-white border"}`} data-testid="company-ac-be">🇧🇪</button>
            <button type="button" onClick={() => { setCountry("FR"); setRows([]); }} className={`text-[10px] px-1.5 py-0.5 rounded-full ${country === "FR" ? "bg-indigo-600 text-white" : "bg-white border"}`} data-testid="company-ac-fr">🇫🇷</button>
          </div>
        </div>
      )}
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Tape le nom (min 3 lettres) ou n° TVA…"
          value={q}
          onChange={(e) => search(e.target.value)}
          className="pl-8 h-9 text-sm"
          data-testid="company-ac-input"
        />
        {rows.length > 0 && (
          <div className="absolute z-30 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {rows.map((s, i) => (
              <button
                key={s.enterprise_number || s.siret || i}
                type="button"
                onClick={() => pick(s)}
                className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-xs border-b last:border-0"
                data-testid={`company-ac-item-${i}`}
              >
                <div className="font-semibold text-slate-700">{s.nom}</div>
                <div className="text-slate-400">{s.tva} · {s.adresse}, {s.code_postal} {s.ville}</div>
              </button>
            ))}
          </div>
        )}
      </div>
      {loading && <p className="text-[10px] text-slate-400 px-1">Recherche…</p>}
    </div>
  );
};

export default CompanyAutocomplete;
